import { GenerateSlidesModal } from "@/components/lessonGenerator/GenerateSlidesModal";
import {
  EMPTY_SLIDE_FILTERS,
  NONE,
  SlideFilterBar,
  type SlideFilterState,
  hasActiveFilter,
} from "@/components/lessons/SlideFilterBar";
import { DuplicateSlideModal } from "@/components/modal/DuplicateSlideModal";
import { HideModal } from "@/components/modal/HideModal";
import { MoveSlideModal } from "@/components/modal/MoveSlideModal";
import { SelectionActionModal } from "@/components/modal/SelectionActionModal";
import { SetSlideDomainModal } from "@/components/modal/SetSlideDomainModal";
import { SetSlideTagsModal } from "@/components/modal/SetSlideTagsModal";
import { TrashModal } from "@/components/modal/TrashModal";
import { UnlinkModal } from "@/components/modal/UnlinkModal";
import { WarningModal } from "@/components/modal/WarningModal";
import { ActionButtons } from "@/components/projects/ActionButtons";
import { NoItemFound } from "@/components/shared/NoItemFound";
import { NotFound } from "@/components/shared/NotFound";
import { StickyAddButton } from "@/components/shared/StickyAddButton";
import { MainSidebar } from "@/components/sidebars/MainSidebar";
import { SlideCardSkeleton } from "@/components/skeletons";
import { BreadcrumbSkeleton } from "@/components/skeletons/BreadcrumbSkeleton";
import { AddSlideModal } from "@/components/slides/list/AddSlideModal";
import { MergeModeActionBar } from "@/components/slides/list/MergeModeActionBar";
import { NestSlideModal } from "@/components/slides/list/NestSlideModal";
import { SlideCard } from "@/components/slides/list/SlideCard";
import { SelectionProvider, useSelection } from "@/contexts/SelectionContext";
import { GetLessonBreadcrumbsDocument } from "@/generated/graphql";
import { ITEM_LIMIT } from "@/lib/constants/shared";
import type { ReorderInfo, SortableHandleProps } from "@/lib/dnd/components/Sortable";
import { Sortable } from "@/lib/dnd/components/Sortable";
import { useGeneratingSlideAudio } from "@/lib/hooks/lessonGenerator/useLessonGeneration";
import { useHealMissingThumbnails } from "@/lib/hooks/slides/useHealMissingThumbnails";
import { useReorderSlide } from "@/lib/hooks/slides/useReorderSlide";
import {
  type SlideWithSubslides,
  useSlidesByLessonId,
} from "@/lib/hooks/slides/useSlidesByLessonId";
import { useTags } from "@/lib/hooks/tags/useTags";
import { useResponsiveSkeletonCount } from "@/lib/hooks/useResponsiveSkeletonCount";
import { useScrollRestoration } from "@/lib/hooks/useScrollRestoration";
import { hasGraphQLErrorCode } from "@/lib/utils/graphqlErrors";
import { getSlideReorderAnchors } from "@/lib/utils/slideReorderAnchors";
import { unslugTagId } from "@/lib/utils/tagLabel";
import { useDebouncedSearch } from "@/lib/utils/useDebouncedSearch";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { openEditSlideModal } from "@/store/uiSlice";
import type { SlideFields } from "@/types/Slide";
import type { ViewMode } from "@/types/viewMode";
import { ErrorCodes, resolveSlideTemplate } from "@ai-teach/types";
import { Breadcrumb, Button } from "@ai-teach/ui-web";
import { SearchBar } from "@ai-teach/ui-web";
import { useQuery } from "@apollo/client/react";
import { Link, useParams } from "@tanstack/react-router";
import { useRef, useState } from "react";

// Lesson sections — one per tag, in taxonomy order. A slide lands in exactly one section,
// keyed by its first tag, so a multi-tagged slide is never listed twice.
interface SectionConfig {
  key: string;
  label: string;
  color: string;
}

/** Sections that stand for no live tag: archived ones, and the untagged catch-all. */
const NEUTRAL_SECTION_COLOR = "#9ca3af";

const UNTAGGED_SECTION: SectionConfig = {
  key: "untagged",
  label: "Untagged",
  color: NEUTRAL_SECTION_COLOR,
};

const primaryTagId = (slide: SlideWithSubslides) => slide.tagIds?.[0];

function groupSlidesIntoSections(
  slides: SlideWithSubslides[],
  tags: readonly { id: string; name: string; color: string }[]
): { section: SectionConfig; slides: SlideWithSubslides[] }[] {
  const live = new Set(tags.map((tag) => tag.id));
  // Archived tags leave the taxonomy but stay on slides, so give them a section of their own
  // rather than dropping the slides that carry nothing else.
  const archived = [
    ...new Set(
      slides.flatMap((slide) => {
        const id = primaryTagId(slide);
        return id && !live.has(id) ? [id] : [];
      })
    ),
  ].sort();

  const sections: SectionConfig[] = [
    ...tags.map((tag) => ({ key: tag.id, label: tag.name, color: tag.color })),
    ...archived.map((id) => ({ key: id, label: unslugTagId(id), color: NEUTRAL_SECTION_COLOR })),
  ];

  const result = sections
    .map((section) => ({
      section,
      slides: slides.filter((slide) => primaryTagId(slide) === section.key),
    }))
    .filter(({ slides: sectionSlides }) => sectionSlides.length > 0);

  const untagged = slides.filter((slide) => !slide.tagIds?.length);
  if (untagged.length > 0) result.push({ section: UNTAGGED_SECTION, slides: untagged });

  return result;
}

const STATUS_SERVER_FILTERS = {
  incomplete: { isCompleted: false },
  visible: { isHidden: false },
  hidden: { isHidden: true },
} as const satisfies Record<NonNullable<SlideFilterState["status"]>, object>;

function LessonDetailPageContent() {
  const params = useParams({ from: "/admin/lesson/$lessonId/" });
  const lessonId = params.lessonId;

  const dispatch = useAppDispatch();

  const skeletonCount = useResponsiveSkeletonCount(2);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [searchInput, setSearchInput] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [filters, setFilters] = useState<SlideFilterState>(EMPTY_SLIDE_FILTERS);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);

  const debouncedSearch = useDebouncedSearch(searchInput);

  const { tags } = useTags();

  // Every dimension the API can narrow itself. Template, "no domain" and tags have no
  // server-side equivalent, so those are applied to the loaded lesson below.
  const serverFilters = {
    ...(filters.status ? STATUS_SERVER_FILTERS[filters.status] : {}),
    domain: filters.domain === NONE ? null : filters.domain,
  };

  const {
    data: lessonBreadcrumbsData,
    loading: breadcrumbsLoading,
    error: breadcrumbsError,
  } = useQuery(GetLessonBreadcrumbsDocument, {
    variables: { id: lessonId },
    fetchPolicy: "cache-first",
  });

  if (hasGraphQLErrorCode(breadcrumbsError, ErrorCodes.NOT_FOUND)) {
    return (
      <NotFound
        title="Lesson not found"
        description="This lesson doesn't exist or the ID is invalid."
      />
    );
  }

  const lessonBreadcrumbs = lessonBreadcrumbsData?.lesson?.breadcrumbs;

  const {
    lessonSlideItems,
    lessonSlidesLoading,
    lessonSlidesError,
    refetchLessonSlides,
    lessonSlidesHasMore,
  } = useSlidesByLessonId({
    lessonId,
    searchText: debouncedSearch,
    isTrash: false,
    ...serverFilters,
  });

  const generatingAudioSlideIds = useGeneratingSlideAudio(lessonId, refetchLessonSlides);

  useHealMissingThumbnails(lessonSlideItems);

  // The hook loads the whole lesson, so narrowing here sees every slide, not just a page.
  const visibleSlides = lessonSlideItems.filter((slide) => {
    if (filters.template && resolveSlideTemplate(slide) !== filters.template) return false;
    if (filters.domain === NONE && slide.domains?.length) return false;
    if (filters.tagId === NONE) return !slide.tagIds?.length;
    if (filters.tagId) return !!slide.tagIds?.includes(filters.tagId);
    return true;
  });

  const { isEditSlideModalOpen } = useAppSelector((state) => state.ui);

  // Collapsed by default: a group shows as one stacked card until an editor opens it.
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());

  const toggleGroup = (slideId: string) =>
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(slideId)) next.add(slideId);
      return next;
    });

  const renderSlideCard = (
    slide: SlideWithSubslides,
    index: number,
    dragHandle?: SortableHandleProps
  ) => (
    <SlideCard
      slide={slide}
      slideNumber={index + 1}
      url="lesson"
      viewMode={viewMode}
      subslideCount={slide.children.length}
      isSubslidesExpanded={expandedGroupIds.has(slide.id)}
      onToggleSubslides={() => toggleGroup(slide.id)}
      isGeneratingAudio={generatingAudioSlideIds.has(slide.id)}
      dragHandle={dragHandle}
    />
  );

  // Subslides render as their own grid cells beside the main slide, tinted so they never read as
  // ordinary lesson slides.
  const renderSubslides = (slide: SlideWithSubslides) => {
    if (!expandedGroupIds.has(slide.id) || slide.children.length === 0) return null;

    // Its own sortable list: `sortOrder` is scoped to the group, so a subslide can only be
    // dragged against its siblings — the server rejects any other anchor.
    return (
      <Sortable
        items={slide.children}
        onReorder={onSubslideReorder}
        disabled={isSelectionMode}
        handle
      >
        {(child, childIndex, dragHandle) => (
          <SlideCard
            slide={child}
            slideNumber={childIndex + 1}
            url="lesson"
            viewMode={viewMode}
            isSubslide
            isGeneratingAudio={generatingAudioSlideIds.has(child.id)}
            dragHandle={dragHandle}
          />
        )}
      </Sortable>
    );
  };

  useScrollRestoration(lessonId, lessonSlidesLoading || lessonSlideItems.length === 0);

  const { reorderSlide } = useReorderSlide();

  const handleAddNewSlide = () => {
    dispatch(openEditSlideModal(null));
  };

  // A group is a self-contained list, so the dropped slide's new neighbours ARE its anchors.
  const onSubslideReorder = (reorderedSubslides: SlideFields[], { newIndex }: ReorderInfo) => {
    const movedSubslide = reorderedSubslides[newIndex];
    if (!movedSubslide) return;

    const prevSortOrder = reorderedSubslides[newIndex - 1]?.sortOrder ?? null;
    const nextSortOrder = reorderedSubslides[newIndex + 1]?.sortOrder;
    const newSortOrder = nextSortOrder != null ? ((prevSortOrder ?? 0) + nextSortOrder) / 2 : null;

    return reorderSlide(movedSubslide, lessonId, prevSortOrder, newSortOrder);
  };

  // dnd hands back the reordered slice it rendered — one tag section in the sectioned view.
  const onSlideReorder = (reorderedSlides: SlideFields[], { newIndex }: ReorderInfo) => {
    const movedSlide = reorderedSlides[newIndex];
    if (!movedSlide) return;

    const { prevSortOrder, newSortOrder } = getSlideReorderAnchors({
      movedSlideId: movedSlide.id,
      sectionPrev: reorderedSlides[newIndex - 1],
      sectionNext: reorderedSlides[newIndex + 1],
      lessonSlides: lessonSlideItems,
    });

    return reorderSlide(movedSlide, lessonId, prevSortOrder, newSortOrder);
  };

  const {
    isMoveSlideModalOpen,
    isDuplicateSlideModalOpen,
    isSetTagsModalOpen,
    isSetDomainModalOpen,
    isTrashModalOpen,
    isHideModalOpen,
    isSelectionMode,
    isMergeMode,
    isUnlinkModalOpen,
    isWarningModalOpen,
  } = useSelection();

  return (
    <div className="flex h-screen">
      <MainSidebar />

      {/* Main content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-6">
          {/* Breadcrumb Navigation */}
          {breadcrumbsLoading ? (
            <BreadcrumbSkeleton className="mb-5" />
          ) : (
            <Breadcrumb
              items={[
                {
                  label: "Home",
                  to: "/admin",
                },
                ...(lessonBreadcrumbs?.map((breadcrumb, index) => {
                  const isLast = index === lessonBreadcrumbs.length - 1;
                  return {
                    label: breadcrumb.name,
                    ...(isLast ? {} : { to: `/admin/project/${breadcrumb.id}` }),
                  };
                }) ?? []),
              ]}
              className="mb-4"
            />
          )}

          {/* Lesson Title */}
          <h1 className="text-2xl font-bold w-[55%] truncate my-5">
            {breadcrumbsLoading ? (
              <span className="inline-block h-9 w-64 bg-slate-200 animate-pulse rounded" />
            ) : (
              (lessonBreadcrumbs?.at(-1)?.name ?? "")
            )}
          </h1>

          {/* Page Header */}
          <div className="flex justify-between items-end mb-4">
            <SearchBar
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search..."
              className="w-1/4"
            />

            <div className="flex items-end gap-3">
              <Link to="/admin/lesson/$lessonId/drafts" params={{ lessonId }}>
                <Button variant="ghost">Drafts</Button>
              </Link>
              <Button variant="secondary" onClick={() => setIsGenerateOpen(true)}>
                Generate Slides
              </Button>
              <ActionButtons
                trashTo="/admin/lesson/$lessonId/trash"
                trashParams={{ lessonId }}
                trashCount={lessonBreadcrumbsData?.lesson?.trashCount ?? 0}
                viewMode={viewMode}
                onToggleView={() => setViewMode((prev) => (prev === "grid" ? "list" : "grid"))}
                onAdd={handleAddNewSlide}
              />
            </div>
          </div>

          <SlideFilterBar
            filters={filters}
            setFilters={setFilters}
            className="mb-6 max-sm:hidden"
          />

          {/* Slides — sectioned when no filter/search, flat sortable otherwise */}
          {!lessonSlidesLoading && visibleSlides.length === 0 ? (
            <NoItemFound itemType="slide" error={lessonSlidesError} refetch={refetchLessonSlides} />
          ) : !hasActiveFilter(filters) && debouncedSearch.trim() === "" ? (
            /* ── Sectioned view ── */
            <>
              {lessonSlidesLoading && lessonSlideItems.length === 0 ? (
                <div className={ITEM_LIMIT.slideGridCardItemLimitClass}>
                  {Array.from({ length: skeletonCount }).map((_, i) => (
                    <SlideCardSkeleton key={`slide-skeleton-card-${i + 1}`} />
                  ))}
                </div>
              ) : (
                groupSlidesIntoSections(visibleSlides, tags).map(
                  ({ section, slides: sectionSlides }) => (
                    <div key={section.key} className="mb-10">
                      {/* Section header — tinted with the tag's own color. */}
                      <div
                        className="flex items-center gap-3 px-4 py-2.5 mb-4 rounded-lg border"
                        style={{
                          backgroundColor: `${section.color}14`,
                          borderColor: `${section.color}59`,
                        }}
                      >
                        <div
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: section.color }}
                        />
                        <h2
                          className="font-semibold text-sm uppercase tracking-wider"
                          style={{ color: section.color }}
                        >
                          {section.label}
                        </h2>
                        <span
                          className="ml-auto text-xs font-medium opacity-70"
                          style={{ color: section.color }}
                        >
                          {sectionSlides.length} {sectionSlides.length === 1 ? "slide" : "slides"}
                        </span>
                      </div>

                      {/* Section slides — sortable within each section */}
                      <div
                        className={
                          viewMode === "grid"
                            ? ITEM_LIMIT.slideGridCardItemLimitClass
                            : ITEM_LIMIT.listCardItemLimitClass
                        }
                      >
                        <Sortable
                          items={sectionSlides}
                          onReorder={onSlideReorder}
                          disabled={isSelectionMode}
                          after={renderSubslides}
                          handle
                        >
                          {(slide, index, dragHandle) => renderSlideCard(slide, index, dragHandle)}
                        </Sortable>
                      </div>
                    </div>
                  )
                )
              )}

              {/* The hook fetches the whole lesson on its own; this only shows that a lesson long
                  enough to exceed one page is still filling in. */}
              {lessonSlidesHasMore && (
                <div className="flex justify-center py-8">
                  <div className="flex items-center gap-2 text-muted">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span>Loading remaining slides...</span>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* ── Flat view when filter or search is active ── */
            <>
              <div
                className={
                  viewMode === "grid"
                    ? ITEM_LIMIT.slideGridCardItemLimitClass
                    : ITEM_LIMIT.listCardItemLimitClass
                }
              >
                {lessonSlidesLoading && lessonSlideItems.length === 0 ? (
                  <>
                    {Array.from({ length: skeletonCount }).map((_, i) => (
                      <SlideCardSkeleton key={`slide-skeleton-card-${i + 1}`} />
                    ))}
                  </>
                ) : (
                  <Sortable
                    items={visibleSlides}
                    onReorder={onSlideReorder}
                    disabled={isSelectionMode || debouncedSearch.trim() !== ""}
                    after={renderSubslides}
                    handle
                  >
                    {(slide, index, dragHandle) => renderSlideCard(slide, index, dragHandle)}
                  </Sortable>
                )}
              </div>

              {/* The hook fetches the whole lesson on its own; this only shows that a lesson long
                  enough to exceed one page is still filling in. */}
              {lessonSlidesHasMore && (
                <div className="flex justify-center py-8">
                  <div className="flex items-center gap-2 text-muted">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span>Loading remaining slides...</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <StickyAddButton onClick={handleAddNewSlide} scrollContainerRef={scrollContainerRef} />

      {/* Modals */}
      {isMoveSlideModalOpen && (
        <MoveSlideModal
          isOpen={isMoveSlideModalOpen}
          sourceLessonId={lessonId}
          defaultProjectStack={lessonBreadcrumbs
            ?.slice(0, -1)
            .map((b) => ({ id: b.id, name: b.name }))}
        />
      )}
      {isTrashModalOpen && <TrashModal isOpen={isTrashModalOpen} sourceId={lessonId} />}
      {isHideModalOpen && <HideModal mode={isHideModalOpen} />}
      {isSelectionMode && (
        <SelectionActionModal
          page="lessonPage"
          isOpen={isSelectionMode}
          slides={lessonSlideItems}
        />
      )}
      {isMergeMode && <MergeModeActionBar slides={lessonSlideItems} />}
      {isDuplicateSlideModalOpen && (
        <DuplicateSlideModal
          isOpen={true}
          defaultLessonId={lessonId}
          defaultProjectStack={lessonBreadcrumbs
            ?.slice(0, -1)
            .map((b) => ({ id: b.id, name: b.name }))}
        />
      )}
      {isSetTagsModalOpen && <SetSlideTagsModal isOpen={true} />}
      {isSetDomainModalOpen && <SetSlideDomainModal isOpen={true} />}
      {isUnlinkModalOpen && <UnlinkModal isOpen={true} />}
      {isEditSlideModalOpen && <AddSlideModal lessonId={lessonId} />}
      {/* Mounted only once the breadcrumbs are known: the reference picker reads the project stack
          in a state initializer, so one arriving later would be ignored. */}
      {isGenerateOpen && !breadcrumbsLoading && (
        <GenerateSlidesModal
          isOpen={isGenerateOpen}
          onClose={() => setIsGenerateOpen(false)}
          lessonId={lessonId}
          onSlidesAdded={refetchLessonSlides}
        />
      )}
      <NestSlideModal mainSlides={lessonSlideItems} />
      {isWarningModalOpen && <WarningModal isOpen={!!isWarningModalOpen} />}
    </div>
  );
}

export function LessonDetailPage() {
  return (
    <SelectionProvider>
      <LessonDetailPageContent />
    </SelectionProvider>
  );
}
